import { useEffect } from 'react';

// Keep the global `--sticky-filter-h` CSS variable in sync with the height of
// the page's sticky filter bar (`.docs-filter-wrap`). The sticky table header
// docks at `calc(60px + var(--sticky-filter-h))` so it sits directly beneath
// the filter bar instead of being hidden behind it (both stay visible while
// scrolling a long list).
//
// Pass a ref to the filter-bar wrapper element. The height is re-measured on
// resize (filter chips wrapping to a new line changes it) and the variable is
// reset to 0 on unmount so pages without a filter bar dock the header right
// under the navbar.
export function useStickyFilterOffset(ref) {
  useEffect(() => {
    const root = document.documentElement;
    const write = () => {
      const el = ref.current;
      root.style.setProperty('--sticky-filter-h', el ? `${el.offsetHeight}px` : '0px');
    };
    // Every filter bar renders a DIFFERENT DOM tree above and below the mobile
    // breakpoint, so the wrapper node is REPLACED when the viewport crosses it.
    // Observing one element for the effect's lifetime would leave the observer
    // pointed at a detached node — the variable froze and the sticky header
    // docked at the wrong offset until reload. So re-point the observer at
    // whatever ref.current currently is.
    const ro = new ResizeObserver(write);
    let observed = null;
    const sync = () => {
      write();
      if (ref.current !== observed) {
        if (observed) ro.unobserve(observed);
        observed = ref.current;
        if (observed) ro.observe(observed);
      }
    };
    // The resize event fires DURING the browser's resize, before React has
    // committed the swapped-in node — so a sync() during the event still sees
    // the old ref.current. Defer to the next frame, by which point React has
    // committed and ref.current points at the new node. (Content-reflow that
    // doesn't swap the node — e.g. filter chips wrapping — is already covered by
    // the ResizeObserver, so no immediate read is needed here; deferring also
    // keeps the offsetHeight read out of the resize event's synchronous path.)
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(sync);
    };
    sync();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', onResize);
      root.style.setProperty('--sticky-filter-h', '0px');
    };
  }, [ref]);
}
